/**
 * Route to Resource mapping for database-driven RBAC
 * Maps frontend routes to backend resource types
 */

export interface RouteResourceMapping {
  resource: string;
  action?: string;
}

/**
 * Map routes to required resource permissions
 * This connects frontend routes with backend RBAC permissions
 */
export const routeResourceMap: Record<string, RouteResourceMapping> = {
  '/dashboard': { resource: 'setting', action: 'read' }, // Dashboard requires read access to settings
  '/alumni': { resource: 'alumni', action: 'read' },
  '/alumni/:id': { resource: 'alumni', action: 'read' },
  '/alumni/edit/:id': { resource: 'alumni', action: 'update' },
  '/news': { resource: 'news', action: 'read' },
  '/admin-users': { resource: 'user', action: 'read' }, // Admin users are in 'user' resource
  '/alumni-users': { resource: 'user', action: 'read' }, // Alumni users are also 'user' resource
  '/profile': { resource: 'user', action: 'read' }, // Own profile
  '/master-data': { resource: 'setting', action: 'update' }, // Master data management
  '/line-bot': { resource: 'line_bot', action: 'update' }, // LINE Bot management
  '/messages': { resource: 'setting', action: 'read' }, // Message management (allow same as dashboard)
  '/legal-documents': { resource: 'setting', action: 'update' }, // Legal documents management
  '/settings': { resource: 'setting', action: 'update' }, // Settings requires update permission
  '/rbac': { resource: 'setting', action: 'update' }, // RBAC is system settings
  '/logs': { resource: 'activity_log', action: 'read' },
};

/**
 * Fallback role-based access for routes not yet mapped to resources
 * Used as temporary solution during migration
 */
export type AdminRole = 'super_admin' | 'admin' | 'moderator';

export const fallbackRoleAccess: Record<string, AdminRole[]> = {
  '/dashboard': ['super_admin', 'admin', 'moderator'],
  '/alumni': ['super_admin', 'admin', 'moderator'],
  '/alumni/:id': ['super_admin', 'admin', 'moderator'],
  '/alumni/edit/:id': ['super_admin', 'admin', 'moderator'],
  '/news': ['super_admin', 'admin', 'moderator'],
  '/admin-users': ['super_admin', 'admin'],
  '/alumni-users': ['super_admin', 'admin', 'moderator'],
  '/profile': ['super_admin', 'admin', 'moderator'],
  '/master-data': ['super_admin', 'admin'],
  '/line-bot': ['super_admin', 'admin'],
  '/messages': ['super_admin', 'admin', 'moderator'],
  '/legal-documents': ['super_admin', 'admin'],
  '/settings': ['super_admin', 'admin'],
  '/rbac': ['super_admin'],
  '/logs': ['super_admin'],
};

/**
 * Check if user has access based on basic role (fallback)
 */
export const hasRoleAccess = (userRole: string | undefined, route: string): boolean => {
  if (!userRole) return false;
  const allowedRoles = fallbackRoleAccess[route];
  if (!allowedRoles) return false;
  return allowedRoles.includes(userRole as AdminRole);
};
